import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, authHeaders } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { ArrowLeft, ShoppingBag, Truck, MapPin } from 'lucide-react';
import { formatAuPhone, PHONE_PLACEHOLDER } from '@/utils/phone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FACTORY_ADDRESS = {
  street: '100 Test St',
  suburb: 'Footscray',
  state: 'VIC',
  postcode: '3011',
  full: '100 Test St, Footscray VIC 3011'
};

export default function CheckoutPage() {
  const { user } = useAuth();
  const { cart, sessionId, clearCart } = useCart();
  const items = cart.items || [];
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [deliveryOption, setDeliveryOption] = useState('delivery');
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    line1: '', line2: '', city: '', state: '', postcode: '', country: 'Australia',
    notes: '',
  });

  useEffect(() => {
    if (user) {
      setForm(f => ({
        ...f,
        name: f.name || user.name || '',
        email: f.email || user.email || '',
        phone: f.phone || user.phone || '',
        line1: f.line1 || user.address?.line1 || '',
        line2: f.line2 || user.address?.line2 || '',
        city: f.city || user.address?.city || '',
        state: f.state || user.address?.state || '',
        postcode: f.postcode || user.address?.postcode || '',
        country: f.country || user.address?.country || 'Australia',
      }));
    }
  }, [user]);

  const subtotal = items.reduce((sum, item) => sum + (item.total_price || 0), 0);
  const shippingCost = deliveryOption === 'pickup' ? 0 : (subtotal >= 400 ? 0 : 29.95);
  const total = subtotal + shippingCost;

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { toast.error('Name, email, and phone are required'); return; }
    if (deliveryOption === 'delivery' && (!form.line1 || !form.city || !form.state || !form.postcode)) { toast.error('Please fill in all shipping address fields'); return; }
    if (items.length === 0) { toast.error('Your cart is empty'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/orders/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          session_id: sessionId,
          origin_url: window.location.origin,
          customer_name: form.name,
          customer_email: form.email,
          customer_phone: form.phone,
          delivery_option: deliveryOption,
          pickup_address: deliveryOption === 'pickup' ? FACTORY_ADDRESS.full : null,
          address_line1: form.line1,
          address_line2: form.line2,
          address_city: form.city,
          address_state: form.state,
          address_postcode: form.postcode,
          address_country: form.country,
          notes: form.notes,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || 'Failed to place order');
        setLoading(false);
        return;
      }
      const data = await res.json();
      clearCart();
      toast.success('Order placed successfully!');
      navigate(`/order/${data.order_id}${!user ? `?email=${encodeURIComponent(form.email)}` : ''}`, { replace: true });
    } catch {
      toast.error('Something went wrong');
    }
    setLoading(false);
  };

  if (items.length === 0) {
    return (
      <div className="pt-20 sm:pt-[122px] min-h-[60vh] flex items-center justify-center px-4" data-testid="checkout-empty">
        <div className="text-center">
          <ShoppingBag className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-2">Your cart is empty</p>
          <Link to="/products" className="text-teal-600 text-sm hover:underline">Browse products</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 sm:pt-[122px] min-h-screen bg-cream" data-testid="checkout-page">
      <section className="bg-white py-10 sm:py-16 border-b border-cream-dark">
        <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
          <h1 className="text-4xl sm:text-5xl font-display font-semibold text-[#1A1A1A] mb-4 tracking-tight">Checkout</h1>
          <p className="text-base sm:text-lg text-gray-500 max-w-2xl font-body">Complete your order details and place your order.</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6 sm:py-12">
        <Link to="/cart" className="inline-flex items-center text-sm text-gray-500 hover:text-teal-600 mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to cart
        </Link>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Form */}
          <form onSubmit={handlePlaceOrder} className="lg:col-span-3 space-y-5">
            <div className="bg-white rounded-xl shadow-card p-5 sm:p-6">
              <h2 className="text-base font-display font-semibold mb-4">Contact Details</h2>
              <div className="space-y-3">
                <div><Label className="text-sm font-medium">Full Name *</Label><Input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 h-11" data-testid="checkout-name" /></div>
                <div><Label className="text-sm font-medium">Email *</Label><Input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 h-11" data-testid="checkout-email" /></div>
                <div><Label className="text-sm font-medium">Phone *</Label><Input required value={form.phone} onChange={e => setForm({...form, phone: formatAuPhone(e.target.value)})} placeholder={PHONE_PLACEHOLDER} className="mt-1 h-11" data-testid="checkout-phone" /></div>
              </div>
            </div>

            {/* Delivery Option */}
            <div className="bg-white rounded-xl shadow-card p-5 sm:p-6">
              <h2 className="text-base font-display font-semibold mb-4">Delivery Option</h2>
              <RadioGroup
                value={deliveryOption}
                onValueChange={setDeliveryOption}
                className="space-y-3"
                data-testid="delivery-options"
              >
                <label
                  className={`flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    deliveryOption === 'delivery'
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="delivery-option-shipping"
                >
                  <RadioGroupItem value="delivery" className="mt-0.5" />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center gap-2">
                      <Truck className="h-4 w-4 text-teal-600" />
                      <span className="font-semibold text-sm">Ship to Address</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {subtotal >= 400 ? (
                        <span className="text-teal-600 font-medium">Free shipping!</span>
                      ) : (
                        <span>$29.95 (Free over $400)</span>
                      )}
                    </p>
                  </div>
                </label>

                <label
                  className={`flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    deliveryOption === 'pickup'
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="delivery-option-pickup"
                >
                  <RadioGroupItem value="pickup" className="mt-0.5" />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-teal-600" />
                      <span className="font-semibold text-sm">Factory Pickup</span>
                      <span className="text-xs bg-teal-50 text-teal-600 px-2 py-0.5 rounded-full font-medium">FREE</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">Collect from our factory</p>
                    <div className="mt-2 p-3 bg-cream rounded-lg">
                      <p className="text-sm font-medium text-[#1A1A1A]">Pickup Address:</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {FACTORY_ADDRESS.street}<br />
                        {FACTORY_ADDRESS.suburb} {FACTORY_ADDRESS.state} {FACTORY_ADDRESS.postcode}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">Mon-Fri: 9am - 5pm</p>
                    </div>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {/* Shipping Address - only show for delivery */}
            {deliveryOption === 'delivery' && (
              <div className="bg-white rounded-xl shadow-card p-5 sm:p-6">
                <h2 className="text-base font-display font-semibold mb-4">Shipping Address</h2>
                <div className="space-y-3">
                  <div><Label className="text-sm font-medium">Address Line 1 *</Label><Input required value={form.line1} onChange={e => setForm({...form, line1: e.target.value})} className="mt-1 h-11" data-testid="checkout-line1" /></div>
                  <div><Label className="text-sm font-medium">Address Line 2</Label><Input value={form.line2} onChange={e => setForm({...form, line2: e.target.value})} className="mt-1 h-11" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-sm font-medium">City *</Label><Input required value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="mt-1 h-11" data-testid="checkout-city" /></div>
                    <div><Label className="text-sm font-medium">State *</Label><Input required value={form.state} onChange={e => setForm({...form, state: e.target.value})} className="mt-1 h-11" data-testid="checkout-state" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-sm font-medium">Postcode *</Label><Input required value={form.postcode} onChange={e => setForm({...form, postcode: e.target.value})} className="mt-1 h-11" data-testid="checkout-postcode" /></div>
                    <div><Label className="text-sm font-medium">Country</Label><Input value={form.country} onChange={e => setForm({...form, country: e.target.value})} className="mt-1 h-11" /></div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-card p-5 sm:p-6">
              <h2 className="text-base font-display font-semibold mb-4">Order Notes</h2>
              <textarea
                value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                placeholder="Any special instructions or notes..."
                className="w-full h-24 rounded-lg border border-gray-200 p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                data-testid="checkout-notes"
              />
            </div>

            <Button type="submit" className="w-full h-12 bg-teal-500 hover:bg-teal-600 text-white font-semibold text-base rounded-full" disabled={loading} data-testid="place-order-btn">
              {loading ? 'Placing Order...' : `Place Order - $${total.toFixed(2)}`}
            </Button>
            {!user && <p className="text-xs text-gray-400 text-center">You're checking out as a guest. <Link to="/login" className="text-teal-600 hover:underline">Sign in</Link> to save your details for next time.</p>}
          </form>

          {/* Order summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-card p-5 sticky top-24">
              <h2 className="text-base font-display font-semibold mb-4">Order Summary</h2>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {items.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <div>
                      <p className="font-medium">{item.cushion_name || 'Item'}</p>
                      {item.fabric_name && <p className="text-xs text-gray-400">Fabric: {item.fabric_name}</p>}
                      <p className="text-xs text-gray-400">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-medium">${(item.total_price || 0).toFixed(2)}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-100 mt-4 pt-3 space-y-1.5">
                <div className="flex justify-between text-sm"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm">
                  <span>Shipping</span>
                  <span className={shippingCost === 0 ? 'text-teal-600 font-medium' : ''}>
                    {deliveryOption === 'pickup' ? 'Pickup - Free' : (shippingCost === 0 ? 'Free' : `$${shippingCost.toFixed(2)}`)}
                  </span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-100"><span>Total</span><span className="text-teal-600">${total.toFixed(2)}</span></div>
              </div>
              {deliveryOption === 'delivery' && subtotal < 400 && (
                <p className="text-xs text-gray-500 mt-3 p-2 bg-cream rounded-lg">
                  Add ${(400 - subtotal).toFixed(0)} more for free shipping, or choose factory pickup!
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
